import { Link2, UserPlus, HelpCircle } from '@/ui/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/ui/components/primitives/dropdown-menu'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { SHARE_LINK_TOOLTIP_COPY, INVITING_FRIENDS_TOOLTIP_COPY } from '@/shared/copy/tooltips'
import { Button } from '@/ui/design-system'

interface AddPlusOneOption {
  id: string
  name: string
}

interface InviteDropdownProps {
  /** Owner-only: open share link modal */
  onInviteWithCode?: () => void
  /** Owner-only: open find-friends flow */
  onFindFriends?: () => void
  /** Primary members for "Add plus-one" submenu. Owners see all; non-owners see only themselves. */
  addPlusOneOptions?: AddPlusOneOption[]
  /** Called when user selects a primary member to add a plus-one for. */
  onAddPlusOne?: (parentMemberId: string, parentMemberName: string) => void
  /** Optional: use a custom trigger (e.g. "Invite" with UserPlus). Defaults to "Invite" button. */
  trigger?: React.ReactNode
  /** Button variant when using default trigger */
  variant?: 'default' | 'outline' | 'ghost' | 'link' | 'destructive'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  className?: string
}

export function InviteDropdown({
  onInviteWithCode,
  onFindFriends,
  addPlusOneOptions,
  onAddPlusOne,
  trigger,
  variant = 'ghost',
  size = 'sm',
  className,
}: InviteDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <Button variant={variant} size={size} className={className ?? 'gap-1.5'}>
            <UserPlus className="h-4 w-4" aria-hidden />
            Add
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {onInviteWithCode && (
        <DropdownMenuItem onClick={onInviteWithCode}>
          <Link2 className="h-4 w-4 mr-2 flex-shrink-0" />
          <span className="flex-1">Share link</span>
          <span
            className="flex-shrink-0 inline-flex"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.preventDefault()}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="What is a share link?"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" align="center" className="max-w-xs">
                <p className="text-sm text-muted-foreground leading-relaxed">{SHARE_LINK_TOOLTIP_COPY}</p>
              </TooltipContent>
            </Tooltip>
          </span>
        </DropdownMenuItem>
        )}
        {onFindFriends && (
        <DropdownMenuItem onClick={onFindFriends}>
          <UserPlus className="h-4 w-4 mr-2 flex-shrink-0" />
          <span className="flex-1">Find Friends</span>
          <span
            className="flex-shrink-0 inline-flex"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.preventDefault()}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="What is Find Friends?"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" align="center" className="max-w-xs">
                <p className="text-sm text-muted-foreground leading-relaxed">{INVITING_FRIENDS_TOOLTIP_COPY}</p>
              </TooltipContent>
            </Tooltip>
          </span>
        </DropdownMenuItem>
        )}
        {addPlusOneOptions && addPlusOneOptions.length > 0 && onAddPlusOne && (
          addPlusOneOptions.length === 1 ? (
            <DropdownMenuItem onClick={() => onAddPlusOne(addPlusOneOptions[0].id, addPlusOneOptions[0].name)}>
              <UserPlus className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>Add plus-one</span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <UserPlus className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>Add plus-one</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {addPlusOneOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.id}
                    onClick={() => onAddPlusOne(opt.id, opt.name)}
                  >
                    Add plus-one for {opt.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
